import type { ConnectorAccessProbeResult, ConnectorDefinition } from "../types";

const PROBE_TIMEOUT_MS = 8000;

/**
 * B&G's Freshservice subdomain. Freshservice is single-tenant — every API call
 * is `https://{domain}.freshservice.com/api/v2/...` — so the domain is supplied
 * as a shared, tenant-wide env var (`FRESHSERVICE_DOMAIN`, e.g.
 * `brasfieldgorrie`), NOT collected per-user. Only each user's API key differs.
 * Mirrors the Egnyte/Quickbase domain-from-env pattern.
 */
export function freshserviceDomain(): string | undefined {
  return process.env.FRESHSERVICE_DOMAIN;
}

/**
 * Base URL for every Freshservice REST v2 call. Exported so the `@workspace/ai`
 * Freshservice client reuses the same host. Returns a well-formed URL only when
 * `FRESHSERVICE_DOMAIN` is set; the connector is filtered out of the enabled set
 * otherwise (see `isEnabled`), so callers never hit the `undefined` host.
 */
export function freshserviceBaseUrl(): string {
  return `https://${freshserviceDomain()}.freshservice.com/api/v2`;
}

/**
 * Numeric id of the agent group new tickets are auto-assigned to (the
 * "Service Desk" group), from the tenant-wide `FRESHSERVICE_DEFAULT_GROUP_ID`
 * env var. Best-effort: returns null when unset or non-numeric, and ticket
 * creation simply omits `group_id`. Group ids are tenant-specific (sandbox
 * "Service Desk" is `34000160991`) and not derivable by name via the API
 * key's scope, so this is config rather than a runtime lookup.
 */
export function freshserviceDefaultGroupId(): number | null {
  const raw = process.env.FRESHSERVICE_DEFAULT_GROUP_ID?.trim();
  if (!raw) {
    return null;
  }
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

async function validateFreshserviceAccess(
  token: string
): Promise<ConnectorAccessProbeResult> {
  const domain = freshserviceDomain();
  if (!domain) {
    return {
      ok: false,
      message:
        "Freshservice is not configured for this environment (missing domain). " +
        "Contact an administrator.",
    };
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  const credentials = Buffer.from(`${token}:X`).toString("base64");

  try {
    const response = await fetch(
      `https://${domain}.freshservice.com/api/v2/tickets?per_page=1`,
      {
        headers: {
          Authorization: `Basic ${credentials}`,
          Accept: "application/json",
        },
        signal: controller.signal,
      }
    );

    if (response.status === 401) {
      return {
        ok: false,
        message:
          "Freshservice rejected the API key. Generate a new one under your Freshservice profile → Profile Settings → API Key, then reconnect.",
      };
    }
    if (!response.ok) {
      return {
        ok: false,
        message: `Freshservice returned status ${response.status}. Verify the API key and try again.`,
      };
    }
    return { ok: true };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        ok: false,
        message: `Freshservice did not respond within ${PROBE_TIMEOUT_MS / 1000}s. Try connecting again.`,
      };
    }
    const detail = err instanceof Error ? err.message : String(err);
    return { ok: false, message: `Could not reach Freshservice: ${detail}` };
  } finally {
    clearTimeout(timeout);
  }
}

export const freshserviceAgentConnector: ConnectorDefinition = {
  id: "freshservice",
  name: "Freshservice - Agent",
  description:
    "View and manage IT support tickets, browse the knowledge base, and — when enabled — create tickets and add notes",
  icon: "freshservice",
  authType: "apikey",
  kind: "custom",
  apiKey: {
    bearerField: "apiKey",
    fields: [
      {
        name: "apiKey",
        label: "API Key",
        type: "password",
        required: true,
        placeholder: "Paste your Freshservice API key",
        helpText:
          "Find it in Freshservice under your profile avatar → Profile Settings → API Key.",
      },
    ],
  },
  isEnabled: () => !!freshserviceDomain(),
  validateAccess: validateFreshserviceAccess,
};
